import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { bimModelApi, bimViewerApi, bimParserApi, bimSpeckleApi, bimCollabApi } from "../../../api/bim/bimService";
import type {
  BimModelDto,
  ModelVersionDto,
  TileMetadata,
  TileStatusDto,
  SpatialTreeNode,
  ElementDetail,
  SectionPlaneState,
  ViewMode,
  TileLoadingMode,
  VersionDiffResult,
  ViewerState,
  CommentThread,
  FederatedModelState,
} from "../../../types/bim-viewer.types";

// ---------- Initial State ----------
const initialState: ViewerState = {
  currentModel: null,
  versions: [],
  selectedVersionId: null,
  tileMetadata: [],
  tilesFetched: false,
  spatialTree: [],
  selectedElementId: null,
  selectedElementDetail: null,
  hiddenIfcClasses: [],
  sectionPlanes: [],
  viewMode: "default",
  diffMode: false,
  diffResult: null,
  diffBaseVersionId: null,
  leftSidebarOpen: true,
  rightSidebarOpen: false,
  loading: false,
  error: null,
  // Speckle streaming state
  speckleStreamId: null,
  speckleRootObjectId: null,
  speckleReady: false,
  speckleConversionStatus: null,
  // Comments
  comments: [],
  commentLoading: false,
  // Tile Loading
  tileLoadingMode: 'idle',
  tileLoadProgress: 0,
  tilesLoaded: 0,
  tilesTotal: 0,
  tileFallbackToIfc: false,

  // Federation
  federationEnabled: false,
  federatedModels: [],
  soloModelId: null,
};

// ---------- Helper ----------
// Gateway formatResponseMessage already unwraps `.response`, so the envelope is:
// { status, statusCode, message, data: <actual payload> }
const unwrapGateway = (res: any) =>
  res.data?.data?.response ?? res.data?.data ?? res.data;

// ---------- Async Thunks ----------

export const fetchModel = createAsyncThunk(
  "viewer/fetchModel",
  async (modelId: number) => {
    const res = await bimModelApi.getModel(modelId);
    const model = unwrapGateway(res) as BimModelDto;
    // Use gateway proxy URL instead of direct Azure SAS URL to avoid CORS issues.
    // The gateway endpoint streams the file server-side from Azure Blob Storage.
    const apiBase = process.env.REACT_APP_API_BASE_URL || "/gateWay/bim";
    model.downloadUrl = `${apiBase}/model/download/${modelId}`;
    return model;
  }
);

export const fetchVersions = createAsyncThunk(
  "viewer/fetchVersions",
  async (modelId: number) => {
    const res = await bimModelApi.getVersions(modelId);
    const raw = unwrapGateway(res);
    return (Array.isArray(raw) ? raw : raw?.list ?? []) as ModelVersionDto[];
  }
);

export const fetchTiles = createAsyncThunk(
  "viewer/fetchTiles",
  async (modelId: number) => {
    const res = await bimViewerApi.getTiles(modelId);
    const raw = unwrapGateway(res);
    return (Array.isArray(raw) ? raw : raw?.list ?? []) as TileMetadata[];
  }
);

export const fetchSpatialTree = createAsyncThunk(
  "viewer/fetchSpatialTree",
  async (modelId: number) => {
    // Fetch spatial structure AND classification summary in parallel
    const [spatialRes, classRes] = await Promise.all([
      bimParserApi.getSpatialStructure(modelId).catch(() => null),
      bimParserApi.getClassificationSummary(modelId).catch(() => null),
    ]);

    const spatialRaw = spatialRes ? unwrapGateway(spatialRes) : null;
    const classRaw = classRes ? unwrapGateway(classRes) : null;

    // Build IFC class nodes from classification summary
    const classNodes: SpatialTreeNode[] = [];
    const classList = Array.isArray(classRaw) ? classRaw : classRaw?.list ?? [];
    for (const cls of classList) {
      if (cls.ifcClass) {
        classNodes.push({
          key: `class-${cls.ifcClass}`,
          title: cls.ifcClass,
          type: "class",
          elementCount: cls.elementCount || 0,
        });
      }
    }

    // Transform spatial structure DTO → SpatialTreeNode[]
    // Backend returns: { projectName, sites: [{ siteName, buildings: [{ buildingName, storeys: [...] }] }] }
    if (spatialRaw && spatialRaw.sites && Array.isArray(spatialRaw.sites)) {
      const tree: SpatialTreeNode[] = [];

      for (let si = 0; si < spatialRaw.sites.length; si++) {
        const site = spatialRaw.sites[si];
        const siteNode: SpatialTreeNode = {
          key: `site-${si}`,
          title: site.siteName || "Default Site",
          type: "site",
          children: [],
        };

        const buildings = site.buildings || [];
        for (let bi = 0; bi < buildings.length; bi++) {
          const bldg = buildings[bi];
          const bldgNode: SpatialTreeNode = {
            key: `bldg-${si}-${bi}`,
            title: bldg.buildingName || "Building",
            type: "building",
            children: [],
          };

          const storeys = bldg.storeys || [];
          let totalElements = 0;
          for (let sti = 0; sti < storeys.length; sti++) {
            const storey = storeys[sti];
            const count = storey.elementCount || 0;
            totalElements += count;
            bldgNode.children!.push({
              key: `storey-${si}-${bi}-${sti}`,
              title: storey.storeyName || `Storey ${sti + 1}`,
              type: "storey",
              elementCount: count,
            });
          }
          bldgNode.elementCount = totalElements;
          siteNode.children!.push(bldgNode);
        }

        // Compute site total from buildings only (before adding IFC Classes)
        const buildingTotal = (siteNode.children || []).reduce(
          (s, c) => s + (c.elementCount || 0),
          0
        );
        siteNode.elementCount = buildingTotal;

        // Add IFC Classes group under the site if we have classification data
        if (classNodes.length > 0) {
          siteNode.children!.push({
            key: "ifc-classes",
            title: "IFC Classes",
            type: "building",
            elementCount: classNodes.reduce((s, c) => s + (c.elementCount || 0), 0),
            children: classNodes,
          });
        }
        tree.push(siteNode);
      }

      return tree;
    }

    // If no spatial structure returned but we have classifications, build a simpler tree
    if (classNodes.length > 0) {
      return [
        {
          key: "root",
          title: "Model",
          type: "site" as const,
          elementCount: classNodes.reduce((s, c) => s + (c.elementCount || 0), 0),
          children: classNodes,
        },
      ];
    }

    return [] as SpatialTreeNode[];
  }
);

export const fetchElementDetail = createAsyncThunk(
  "viewer/fetchElementDetail",
  async (params: {
    elementId: number;
    ifcClass?: string;
    elementName?: string;
    elementType?: string;
    globalId?: string;
    description?: string;
    tag?: string;
  }) => {
    const { elementId, ifcClass, elementName, elementType, globalId, description, tag } = params;

    // Build fallback from mesh userData (always available from web-ifc)
    const fallback: ElementDetail = {
      elementId,
      expressId: elementId,
      globalId: globalId || "",
      ifcClass: ifcClass || "",
      elementName: elementName || ifcClass || `Element #${elementId}`,
      elementType: elementType || "",
      storeyName: "",
      storeyIndex: 0,
      buildingName: "",
      description: description || "",
      isExternal: false,
      isLoadbearing: false,
      propertySets: [],
      materials: [],
    };

    try {
      const res = await bimParserApi.getElement(elementId);
      const raw = unwrapGateway(res);
      if (!raw) return fallback;

      return {
        elementId: raw.elementId,
        expressId: raw.expressId || elementId,
        globalId: raw.globalId || globalId || "",
        ifcClass: raw.ifcClass || ifcClass || "",
        elementName: raw.elementName || elementName || "",
        elementType: raw.elementType || elementType || "",
        storeyName: raw.storeyName || "",
        storeyIndex: raw.storeyId || raw.storeyIndex || 0,
        buildingName: raw.buildingName || "",
        description: raw.description || description || "",
        isExternal: raw.isExternal || false,
        isLoadbearing: raw.isLoadbearing || false,
        propertySets: (raw.propertySets || []).map((ps: any) => ({
          psetName: ps.psetName || ps.propertySetName || "",
          properties: ps.properties || [],
        })),
        materials: (raw.materials || []).map((m: any) => ({
          materialName: m.materialName || "",
          materialCategory: m.category || "",
          layerThickness: m.thickness || 0,
          layerIndex: m.layerIndex || 0,
        })),
      } as ElementDetail;
    } catch {
      return fallback;
    }
  }
);

export const compareVersions = createAsyncThunk(
  "viewer/compareVersions",
  async (data: { modelId: number; versionAId: number; versionBId: number }) => {
    const res = await bimModelApi.compareVersions(data);
    return unwrapGateway(res) as VersionDiffResult;
  }
);

// Speckle streaming thunks
export const fetchSpeckleStream = createAsyncThunk(
  "viewer/fetchSpeckleStream",
  async (modelId: number) => {
    const res = await bimSpeckleApi.getStreamByModel(modelId);
    return unwrapGateway(res);
  }
);

export const convertToSpeckle = createAsyncThunk(
  "viewer/convertToSpeckle",
  async (data: { modelId: number; projectId: number }) => {
    const res = await bimSpeckleApi.convertToSpeckle(data.modelId, data.projectId);
    return unwrapGateway(res);
  }
);

// Comment thunks
export const fetchComments = createAsyncThunk(
  "viewer/fetchComments",
  async (params: { modelId?: number; projectId?: number }) => {
    const res = await bimCollabApi.listComments(params);
    const raw = unwrapGateway(res);
    // Response is { count, noOfPages, list: CommentResponseDto[] }
    const list = Array.isArray(raw) ? raw : raw?.list ?? [];
    // Map CommentResponseDto → CommentThread for Redux state
    return list.map((c: any) => {
      const rawContent = c.content || c.comment_text || c.commentText || "";
      // Parse "[IFCCLASS #expressId] text" format
      const tagMatch = rawContent.match(/^\[([A-Z_]+)(?:\s+#(\d+))?\]\s*([\s\S]*)$/);
      const ifcClass = tagMatch ? tagMatch[1] : "";
      const elementExpressId = tagMatch && tagMatch[2] ? parseInt(tagMatch[2], 10) : undefined;
      const text = tagMatch ? tagMatch[3] : rawContent;
      return {
        commentId: c.commentId || c.id || 0,
        modelId: c.entityId || 0,
        projectId: c.projectId || 0,
        elementExpressId,
        ifcClass,
        text,
        authorId: c.commentedBy || c.user_id || 0,
        authorName: c.commentedByUserName || "",
        resolved: !!(c.resolved && c.resolved > 0),
        createdAt: c.commentedAt || c.createdAt || "",
        updatedAt: c.updatedAt || "",
      };
    }) as CommentThread[];
  }
);

export const createComment = createAsyncThunk(
  "viewer/createComment",
  async (data: {
    modelId: number;
    projectId: number;
    elementExpressId?: number;
    ifcClass?: string;
    text: string;
  }, { dispatch }) => {
    await bimCollabApi.createComment(data);
    // Re-fetch comments list after successful creation
    dispatch(fetchComments({ modelId: data.modelId, projectId: data.projectId }));
    return null;
  }
);

export const resolveComment = createAsyncThunk(
  "viewer/resolveComment",
  async (data: { commentId: number; modelId: number; projectId: number }, { dispatch }) => {
    await bimCollabApi.updateComment({ commentId: data.commentId, resolved: true });
    // Re-fetch comments to get persisted state from DB
    dispatch(fetchComments({ modelId: data.modelId, projectId: data.projectId }));
    return data.commentId;
  }
);

// ---------- Tile Generation Thunks ----------

export const generateTiles = createAsyncThunk(
  "viewer/generateTiles",
  async (data: { modelId: number; lodLevel?: number }) => {
    const res = await bimViewerApi.generateTiles(data.modelId, data.lodLevel);
    return unwrapGateway(res);
  }
);

export const pollTileStatus = createAsyncThunk(
  "viewer/pollTileStatus",
  async (modelId: number) => {
    const res = await bimViewerApi.getTileStatus(modelId);
    const raw = unwrapGateway(res);
    return raw as TileStatusDto;
  }
);

// Discipline → hex color (matches DISCIPLINE_CONFIG in LeftSidebar & federation tinting)
const DISCIPLINE_COLOR_MAP: Record<string, string> = {
  architectural: "#2263b3",
  structural: "#e85347",
  mep_mechanical: "#f4bd0e",
  mep_electrical: "#09c2de",
  mep_plumbing: "#1ee0ac",
  civil: "#816bff",
  combined: "#8094ae",
};

// Federation thunk — fetch all models for a project and build download URLs
export const fetchProjectModels = createAsyncThunk(
  "viewer/fetchProjectModels",
  async (projectId: number) => {
    const res = await bimModelApi.listModels({ projectId, pageSize: 100 });
    const raw = unwrapGateway(res);
    const list = Array.isArray(raw) ? raw : raw?.list ?? [];
    const apiBase = process.env.REACT_APP_API_BASE_URL || "/gateWay/bim";
    return list.map((m: any) => {
      const discipline = m.discipline || "combined";
      return {
        modelId: m.modelId,
        modelName: m.modelName || `Model #${m.modelId}`,
        discipline,
        disciplineColor: DISCIPLINE_COLOR_MAP[discipline] || DISCIPLINE_COLOR_MAP.combined,
        downloadUrl: `${apiBase}/model/download/${m.modelId}`,
        fileSize: m.fileSize || 0,
        visible: true,
        opacity: 1,
        loading: false,
        progress: 0,
        loaded: false,
        error: null,
      };
    }) as FederatedModelState[];
  }
);

// ---------- Slice ----------

const viewerSlice = createSlice({
  name: "viewer",
  initialState,
  reducers: {
    selectElement(state, action: PayloadAction<number | null>) {
      state.selectedElementId = action.payload;
      if (action.payload) {
        state.rightSidebarOpen = true;
      }
    },
    clearSelection(state) {
      state.selectedElementId = null;
      state.selectedElementDetail = null;
      state.rightSidebarOpen = false;
    },
    // Set the selected-element detail directly (used by the APS viewer, which
    // sources properties/materials from the SVF2 derivative rather than our parser).
    setSelectedElementDetail(state, action: PayloadAction<ElementDetail | null>) {
      state.selectedElementDetail = action.payload;
      state.selectedElementId = action.payload ? action.payload.elementId || action.payload.expressId || -1 : null;
      state.rightSidebarOpen = !!action.payload;
    },
    toggleIfcClassVisibility(state, action: PayloadAction<string>) {
      const cls = action.payload;
      const idx = state.hiddenIfcClasses.indexOf(cls);
      if (idx >= 0) {
        state.hiddenIfcClasses.splice(idx, 1);
      } else {
        state.hiddenIfcClasses.push(cls);
      }
    },
    /** Isolate: hide ALL classes except the given one */
    isolateIfcClass(state, action: PayloadAction<{ className: string; allClasses: string[] }>) {
      const { className, allClasses } = action.payload;
      // If already isolated to this class, un-isolate (show all)
      const isAlreadyIsolated =
        state.hiddenIfcClasses.length === allClasses.length - 1 &&
        !state.hiddenIfcClasses.includes(className);
      if (isAlreadyIsolated) {
        state.hiddenIfcClasses = [];
      } else {
        state.hiddenIfcClasses = allClasses.filter((c) => c !== className);
      }
    },
    resetHiddenClasses(state) {
      state.hiddenIfcClasses = [];
    },
    setHiddenClasses(state, action: PayloadAction<string[]>) {
      state.hiddenIfcClasses = action.payload;
    },
    setViewMode(state, action: PayloadAction<ViewMode>) {
      state.viewMode = action.payload;
    },
    addSectionPlane(state, action: PayloadAction<SectionPlaneState>) {
      state.sectionPlanes.push(action.payload);
    },
    removeSectionPlane(state, action: PayloadAction<string>) {
      state.sectionPlanes = state.sectionPlanes.filter(
        (p) => p.id !== action.payload
      );
    },
    toggleSectionActive(state, action: PayloadAction<string>) {
      const plane = state.sectionPlanes.find((p) => p.id === action.payload);
      if (plane) plane.active = !plane.active;
    },
    updateSectionPlane(
      state,
      action: PayloadAction<{ id: string; normal?: [number, number, number]; distance?: number }>
    ) {
      const plane = state.sectionPlanes.find((p) => p.id === action.payload.id);
      if (plane) {
        if (action.payload.normal) plane.normal = action.payload.normal;
        if (action.payload.distance !== undefined)
          plane.distance = action.payload.distance;
      }
    },
    enterDiffMode(state, action: PayloadAction<number>) {
      state.diffMode = true;
      state.diffBaseVersionId = action.payload;
    },
    exitDiffMode(state) {
      state.diffMode = false;
      state.diffResult = null;
      state.diffBaseVersionId = null;
    },
    toggleLeftSidebar(state) {
      state.leftSidebarOpen = !state.leftSidebarOpen;
    },
    toggleRightSidebar(state) {
      state.rightSidebarOpen = !state.rightSidebarOpen;
    },
    setSelectedVersion(state, action: PayloadAction<number>) {
      state.selectedVersionId = action.payload;
    },
    resetViewer() {
      return initialState;
    },
    // Tile loading reducers
    setTileLoadingMode(state, action: PayloadAction<TileLoadingMode>) {
      state.tileLoadingMode = action.payload;
    },
    setTileLoadProgress(state, action: PayloadAction<{ loaded: number; total: number; progress: number }>) {
      state.tilesLoaded = action.payload.loaded;
      state.tilesTotal = action.payload.total;
      state.tileLoadProgress = action.payload.progress;
    },
    setTileFallbackToIfc(state, action: PayloadAction<boolean>) {
      state.tileFallbackToIfc = action.payload;
    },

    // Federation reducers
    toggleFederation(state) {
      state.federationEnabled = !state.federationEnabled;
      if (!state.federationEnabled) {
        state.soloModelId = null;
      }
    },
    setFederationEnabled(state, action: PayloadAction<boolean>) {
      state.federationEnabled = action.payload;
      if (!action.payload) {
        state.soloModelId = null;
      }
    },
    toggleModelVisibility(state, action: PayloadAction<number>) {
      const model = state.federatedModels.find((m) => m.modelId === action.payload);
      if (model) model.visible = !model.visible;
    },
    setModelOpacity(state, action: PayloadAction<{ modelId: number; opacity: number }>) {
      const model = state.federatedModels.find((m) => m.modelId === action.payload.modelId);
      if (model) model.opacity = action.payload.opacity;
    },
    soloModel(state, action: PayloadAction<number>) {
      if (state.soloModelId === action.payload) {
        // Un-solo: restore all visible
        state.soloModelId = null;
      } else {
        state.soloModelId = action.payload;
      }
    },
    showAllModels(state) {
      state.soloModelId = null;
      for (const m of state.federatedModels) m.visible = true;
    },
    hideAllModels(state) {
      state.soloModelId = null;
      for (const m of state.federatedModels) m.visible = false;
    },
    updateFederatedModelProgress(state, action: PayloadAction<{ modelId: number; progress: number }>) {
      const model = state.federatedModels.find((m) => m.modelId === action.payload.modelId);
      if (model) model.progress = action.payload.progress;
    },
    setFederatedModelLoading(state, action: PayloadAction<{ modelId: number; loading: boolean }>) {
      const model = state.federatedModels.find((m) => m.modelId === action.payload.modelId);
      if (model) model.loading = action.payload.loading;
    },
    setFederatedModelLoaded(state, action: PayloadAction<{ modelId: number; loaded: boolean; error?: string }>) {
      const model = state.federatedModels.find((m) => m.modelId === action.payload.modelId);
      if (model) {
        model.loaded = action.payload.loaded;
        model.loading = false;
        model.progress = action.payload.loaded ? 100 : 0;
        model.error = action.payload.error || null;
      }
    },
    setFederatedModelColor(state, action: PayloadAction<{ modelId: number; color: string }>) {
      const model = state.federatedModels.find((m) => m.modelId === action.payload.modelId);
      if (model) model.disciplineColor = action.payload.color;
    },
    applyModelColors(state, action: PayloadAction<Record<number, string>>) {
      for (const m of state.federatedModels) {
        if (action.payload[m.modelId]) {
          m.disciplineColor = action.payload[m.modelId];
        }
      }
    },
  },
  extraReducers: (builder) => {
    // fetchModel
    builder.addCase(fetchModel.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchModel.fulfilled, (state, action) => {
      state.loading = false;
      state.currentModel = action.payload;
    });
    builder.addCase(fetchModel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to load model";
    });

    // fetchVersions
    builder.addCase(fetchVersions.fulfilled, (state, action) => {
      state.versions = action.payload || [];
      if (action.payload?.length > 0) {
        state.selectedVersionId = action.payload[0].versionId;
      }
    });

    // fetchTiles
    builder.addCase(fetchTiles.pending, (state) => {
      // New model: clear stale tiles and don't fall back to IFC until we know
      // whether this model has tiles (prevents a premature web-ifc load that
      // would freeze the tab on large models — or crash on a 598MB one).
      state.tilesFetched = false;
      state.tileMetadata = [];
      state.tileFallbackToIfc = false;
    });
    builder.addCase(fetchTiles.fulfilled, (state, action) => {
      state.tileMetadata = action.payload || [];
      state.tilesFetched = true;
    });
    builder.addCase(fetchTiles.rejected, (state) => {
      // Couldn't fetch tile list → allow IFC fallback
      state.tilesFetched = true;
    });

    // fetchSpatialTree
    builder.addCase(fetchSpatialTree.fulfilled, (state, action) => {
      state.spatialTree = action.payload || [];
    });

    // fetchElementDetail
    builder.addCase(fetchElementDetail.pending, (state) => {
      state.selectedElementDetail = null;
    });
    builder.addCase(fetchElementDetail.fulfilled, (state, action) => {
      state.selectedElementDetail = action.payload;
    });
    builder.addCase(fetchElementDetail.rejected, (state) => {
      // Keep sidebar open — show "unavailable" state via null detail + existing elementId
      state.selectedElementDetail = null;
    });

    // compareVersions
    builder.addCase(compareVersions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(compareVersions.fulfilled, (state, action) => {
      state.loading = false;
      state.diffResult = action.payload;
      state.diffMode = true;
    });
    builder.addCase(compareVersions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to compare versions";
    });

    // Speckle stream
    builder.addCase(fetchSpeckleStream.fulfilled, (state, action) => {
      const stream = action.payload?.stream;
      if (stream) {
        state.speckleStreamId = stream.streamId;
        state.speckleRootObjectId = stream.rootObjectId || null;
        state.speckleReady = !!stream.rootObjectId;
      }
    });

    builder.addCase(convertToSpeckle.fulfilled, (state, action) => {
      if (action.payload) {
        state.speckleStreamId = action.payload.streamId;
        state.speckleConversionStatus = action.payload.status;
      }
    });

    // Comments
    builder.addCase(fetchComments.pending, (state) => {
      state.commentLoading = true;
    });
    builder.addCase(fetchComments.fulfilled, (state, action) => {
      state.commentLoading = false;
      state.comments = action.payload || [];
    });
    builder.addCase(fetchComments.rejected, (state) => {
      state.commentLoading = false;
    });
    builder.addCase(createComment.fulfilled, (state) => {
      // Comments list is re-fetched by the thunk itself after creation
    });
    builder.addCase(resolveComment.fulfilled, (state, action) => {
      const idx = state.comments.findIndex((c) => c.commentId === action.payload);
      if (idx >= 0) state.comments[idx].resolved = true;
    });

    // Tile generation
    builder.addCase(generateTiles.fulfilled, (state) => {
      state.tileLoadingMode = 'loading_lod0';
    });
    builder.addCase(pollTileStatus.fulfilled, (state, action) => {
      const status = action.payload;
      if (status) {
        state.tilesTotal = status.totalTiles || 0;
        state.tilesLoaded = status.readyTiles || 0;
        if (status.totalTiles > 0) {
          state.tileLoadProgress = Math.round((status.readyTiles / status.totalTiles) * 100);
        }
        if (status.status === 'ready') {
          state.tileLoadingMode = 'complete';
        } else if (status.status === 'failed') {
          state.tileLoadingMode = 'idle';
          state.tileFallbackToIfc = true;
        }
      }
    });

    // Federation
    builder.addCase(fetchProjectModels.fulfilled, (state, action) => {
      state.federatedModels = action.payload;
    });
  },
});

export const {
  selectElement,
  clearSelection,
  setSelectedElementDetail,
  toggleIfcClassVisibility,
  isolateIfcClass,
  resetHiddenClasses,
  setHiddenClasses,
  setViewMode,
  addSectionPlane,
  removeSectionPlane,
  toggleSectionActive,
  updateSectionPlane,
  enterDiffMode,
  exitDiffMode,
  toggleLeftSidebar,
  toggleRightSidebar,
  setSelectedVersion,
  resetViewer,
  // Tile loading
  setTileLoadingMode,
  setTileLoadProgress,
  setTileFallbackToIfc,
  // Federation
  toggleFederation,
  setFederationEnabled,
  toggleModelVisibility,
  setModelOpacity,
  soloModel,
  showAllModels,
  hideAllModels,
  updateFederatedModelProgress,
  setFederatedModelLoading,
  setFederatedModelLoaded,
  setFederatedModelColor,
  applyModelColors,
} = viewerSlice.actions;

export default viewerSlice.reducer;
